import { Ref, useMemo, useLayoutEffect, useEffect, Children, isValidElement, ReactElement } from "react";

export function setRef(ref?: Ref<any>, value?: any) {
  if (typeof ref === "function") {
    ref(value);
  } else if (ref) {
    // @ts-ignore
    // eslint-disable-next-line
    ref.current = value;
  }
}

export function useForkRef(refA?: Ref<any>, refB?: Ref<any>) {
  return useMemo(() => {
    if (refA == null && refB == null) {
      return null;
    }
    return (refValue: any) => {
      setRef(refA, refValue);
      setRef(refB, refValue);
    };
  }, [refA, refB]);
}

type TFunction = (data?: any) => any;

export function createChainedFunction(...funcs: TFunction[]) {
  return funcs.reduce(
    (acc, func) => {
      if (func == null) {
        return acc;
      }

      return function chainedFunction(...args: any) {
        // @ts-ignore
        acc.apply(this, args);
        // @ts-ignore
        func.apply(this, args);
      };
    },
    // eslint-disable-next-line
    () => {},
  );
}

export const useEnhancedEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

export function cleanChildren(children: ReactElement[]) {
  return Children.toArray(children).filter(child => isValidElement(child));
}

export const wrapEvent = (theirHandler?: (arg?: any) => void, ourHandler?: (arg?: any) => void) => (event: any) => {
  if (theirHandler) {
    theirHandler(event);
  }

  if (!event.defaultPrevented && ourHandler) {
    ourHandler(event);
  }
};

const focusableElList = [
  "a[href]",
  "area[href]",
  "button:not([disabled])",
  "embed",
  "iframe",
  "input:not([disabled])",
  "object",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "*[tabindex]:not([aria-disabled])",
  "*[contenteditable]",
];

const focusableElSelector = focusableElList.join();

export function getFocusables(element: HTMLElement, keyboardOnly = false) {
  let focusableEls = Array.from(element.querySelectorAll(focusableElSelector));

  // filter out elements with display: none
  focusableEls = focusableEls.filter(focusableEl => window.getComputedStyle(focusableEl).display !== "none");

  if (keyboardOnly === true) {
    focusableEls = focusableEls.filter(focusableEl => focusableEl.getAttribute("tabindex") !== "-1");
  }

  return focusableEls;
}

export function prefixWithHttpIfNecessary(withoutHttp: string, secure?: boolean): string | null {
  if (!withoutHttp) {
    return null;
  }

  if (withoutHttp.trim().length === 0) {
    return null;
  }

  if (withoutHttp.toLowerCase().startsWith("http")) {
    return withoutHttp;
  }

  if (secure) {
    return `https://${withoutHttp}`;
  } else {
    return `http://${withoutHttp}`;
  }
}
